// built-from: /canonical-domain-scenarios v1 impl:canonical-domain-scenarios role:implementation
import {
  accelerateProductChange,
  alignBusinessAndTechnologyOwnership,
  changeFailureRate,
  davidMorgan,
  establishTrustedModernisationDirection,
  kevinOSullivan,
  lumenGridEnergy,
  manualOperationalHandoffs,
  mayaByrne,
  priyaNair,
  productChangeLeadTime,
  selectCredibleFirstPhase,
} from './archetypes.ts';
import {
  analyseEngagementDocuments,
  analyseTechnicalEstate,
  buildActivity,
  businessDomainModel,
  captureCommercialVision,
  commercialVision,
  currentValueStreamModel,
  documentEvidenceSet,
  mapBusinessDomains,
  mapCurrentValueStreams,
  technicalEstateInventory,
} from './activities.ts';

const agreeCommercialVision = buildActivity({
  id: 'lumen-grid-agree-commercial-vision',
  definition: captureCommercialVision,
  owner: davidMorgan,
  participants: [mayaByrne],
  output: commercialVision,
  state: 'in-progress',
  progress: 'Outcomes agreed; two measures still need baselines',
});

const synthesiseSuppliedDocuments = buildActivity({
  id: 'lumen-grid-document-analysis',
  definition: analyseEngagementDocuments,
  owner: davidMorgan,
  participants: [mayaByrne, priyaNair],
  output: documentEvidenceSet,
  state: 'complete',
  progress: '6 documents synthesised; 2 uncertainties surfaced',
});

export const mapPriorityValueStreams = buildActivity({
  id: 'lumen-grid-map-priority-value-streams',
  definition: mapCurrentValueStreams,
  owner: davidMorgan,
  participants: [kevinOSullivan],
  output: currentValueStreamModel,
  state: 'planned',
  progress: 'Workshop participants confirmed',
  question: 'Where does customer onboarding lose time, quality and ownership before the first successful vend?',
  whyNow: 'Document analysis identified manual operational handoffs, but not where delay or rework enters the customer journey.',
  linkedOutcome: alignBusinessAndTechnologyOwnership,
  linkedMeasure: manualOperationalHandoffs,
  inputs: [
    'Agreed commercial vision',
    'Document evidence set',
    'Customer onboarding and first vend scope',
  ],
  completionCriteria: [
    'Current flow and participants are confirmed',
    'Work, wait and quality facts are visible',
    'Assumptions and open questions remain explicit',
    'The returned output is reviewed separately',
  ],
  reviewer: davidMorgan,
});

const discoverBusinessDomains = buildActivity({
  id: 'lumen-grid-discover-business-domains',
  definition: mapBusinessDomains,
  owner: davidMorgan,
  participants: [kevinOSullivan, mayaByrne],
  output: businessDomainModel,
  state: 'awaiting-review',
  progress: 'Initial business model ready for sponsor review',
});

const proposedTechnicalEstateAnalysis = buildActivity({
  id: 'lumen-grid-technical-estate-analysis',
  definition: analyseTechnicalEstate,
  owner: priyaNair,
  participants: [davidMorgan, priyaNair],
  output: technicalEstateInventory,
  state: 'proposed',
  progress: 'Not yet in scope',
  source: 'Suggested by contradictions found across 6 supplied documents',
});

export const lumenGridModernisationJourney = Object.freeze({
  id: 'lumen-grid-platform-modernisation',
  client: lumenGridEnergy,
  programme: 'Platform modernisation',
  consultant: davidMorgan,
  sponsor: mayaByrne,
  goal: establishTrustedModernisationDirection,
  outcomes: [
    accelerateProductChange,
    alignBusinessAndTechnologyOwnership,
    selectCredibleFirstPhase,
  ],
  measures: [productChangeLeadTime, manualOperationalHandoffs, changeFailureRate],
  activities: [
    agreeCommercialVision,
    synthesiseSuppliedDocuments,
    mapPriorityValueStreams,
    discoverBusinessDomains,
    proposedTechnicalEstateAnalysis,
  ],
  evidenceSummary: Object.freeze({
    suppliedDocuments: 6,
    outputsAwaitingReview: 1,
    findingsReadyForSynthesis: 0,
  }),
});

export type LumenGridModernisationJourney = typeof lumenGridModernisationJourney;

export function buildLumenGridModernisationJourney(
  overrides: Partial<LumenGridModernisationJourney> = {},
): LumenGridModernisationJourney {
  return Object.freeze({ ...lumenGridModernisationJourney, ...overrides });
}
